/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import axios from "axios"
import {
  API_DEFAULT_LIMIT,
  API_MERCHANT_BALANCE_LIMIT,
} from "common/constants/misc"
import { CommonAPIError } from "common/constants/network_errors"
import {
  storeDetailConvertor,
  merchantDetailV2Convertor,
} from "common/duck/convertors"
import { MerchantDetail, StoreDetail } from "common/types/models/merchant"
import { resetDateRangeParams } from "common/utils/misc"
import { TroyFoodPortalEndpoint } from "constants/endpoints"
import includes from "ramda/src/includes"
import { Epic, ofType } from "redux-observable"
import { of, throwError, EMPTY } from "rxjs"
import {
  delay,
  switchMap,
  expand,
  reduce,
  catchError,
  mergeMap,
} from "rxjs/operators"
import { EpicDependenciesType, AppState } from "store/store.type"
import { createReducer } from "../create-reducer"
import { MerchantOutlets, MerchantInfo } from "./merchant.type"

const NAMESPACE = "MERCHANT"

// actions
const FETCH_MERCHANT_DETAIL = `${NAMESPACE}/FETCH_MERCHANT_DETAIL`
const FETCH_MERCHANT_DETAIL_SUCCESS = `${NAMESPACE}/FETCH_MERCHANT_DETAIL_SUCCESS`
const FETCH_MERCHANT_DETAIL_FAILED = `${NAMESPACE}/FETCH_MERCHANT_DETAIL_FAILED`
const RESET_MERCHANT_OUTLETS = `${NAMESPACE}/RESET_MERCHANT_OUTLETS`
const FETCH_MERCHANT_OUTLETS = `${NAMESPACE}/FETCH_MERCHANT_OUTLETS`
const FETCH_MERCHANT_OUTLETS_SUCCESS = `${NAMESPACE}/FETCH_MERCHANT_OUTLETS_SUCCESS`
const FETCH_MERCHANT_OUTLETS_FAILED = `${NAMESPACE}/FETCH_MERCHANT_OUTLETS_FAILED`
const NEED_UPDATE_MERCHANT_DETAIL = `${NAMESPACE}/NEED_UPDATE_MERCHANT_DETAIL`
const NEED_UPDATE_STORE_DETAIL = `${NAMESPACE}/NEED_UPDATE_STORE_DETAIL`
const CANCEL_PENDING_REQUESTS = `${NAMESPACE}/CANCEL_PENDING_REQUESTS`
const RESET_DETAIL = `${NAMESPACE}/RESET_DETAIL`
const FETCH_MERCHANT_BALANCE_SUCCESS = `${NAMESPACE}/FETCH_MERCHANT_BALANCE_SUCCESS`
const FETCH_MERCHANT_BALANCE_FAILED = `${NAMESPACE}/FETCH_MERCHANT_BALANCE_FAILED`

export const actions = {
  RESET_MERCHANT_OUTLETS,
  FETCH_MERCHANT_DETAIL,
  FETCH_MERCHANT_DETAIL_SUCCESS,
  FETCH_MERCHANT_DETAIL_FAILED,
  FETCH_MERCHANT_OUTLETS,
  FETCH_MERCHANT_OUTLETS_SUCCESS,
  FETCH_MERCHANT_OUTLETS_FAILED,
  NEED_UPDATE_MERCHANT_DETAIL,
  NEED_UPDATE_STORE_DETAIL,
  CANCEL_PENDING_REQUESTS,
  RESET_DETAIL,
  FETCH_MERCHANT_BALANCE_SUCCESS,
  FETCH_MERCHANT_BALANCE_FAILED,
} as const

type ActionTypes = typeof actions[keyof typeof actions]

// action creators
const resetDetail = () => ({
  type: RESET_DETAIL,
})
const fetchMerchantDetail = (startDate?: string, endDate?: string) => ({
  type: FETCH_MERCHANT_DETAIL,
  payload: {
    startDate,
    endDate,
  },
})
const fetchMerchantDetailSuccess = (merchantDetail: MerchantDetail) => ({
  type: FETCH_MERCHANT_DETAIL_SUCCESS,
  payload: {
    merchantDetail,
  },
})
const fetchMerchantDetailFailed = (error: string) => ({
  type: FETCH_MERCHANT_DETAIL_FAILED,
  payload: {
    error,
  },
})
const needUpdateMerchantDetail = () => ({
  type: NEED_UPDATE_MERCHANT_DETAIL,
})
const needUpdateStoreDetail = () => ({
  type: NEED_UPDATE_STORE_DETAIL,
})
const resetMerchantOutlets = () => ({
  type: RESET_MERCHANT_OUTLETS,
})
const fetchMerchantOutlets = (startDate: string, endDate: string) => ({
  type: FETCH_MERCHANT_OUTLETS,
  payload: {
    startDate,
    endDate,
  },
})
const fetchMerchantOutletsSuccess = (outlets: Array<StoreDetail>) => ({
  type: FETCH_MERCHANT_OUTLETS_SUCCESS,
  payload: {
    outlets,
  },
})
const fetchMerchantOutletsFailed = (error: string) => ({
  type: FETCH_MERCHANT_OUTLETS_FAILED,
  payload: {
    error,
  },
})
const cancelPendingRequests = () => ({
  type: CANCEL_PENDING_REQUESTS,
})
const fetchMerchantBalanceSuccess = (balance: PriceInMin) => ({
  type: FETCH_MERCHANT_BALANCE_SUCCESS,
  payload: {
    balance,
  },
})
const fetchMerchantBalanceFailed = () => ({
  type: FETCH_MERCHANT_BALANCE_FAILED,
})
export const actionCreator = {
  resetDetail,
  fetchMerchantDetail,
  fetchMerchantDetailSuccess,
  fetchMerchantDetailFailed,
  resetMerchantOutlets,
  fetchMerchantOutlets,
  fetchMerchantOutletsSuccess,
  fetchMerchantOutletsFailed,
  needUpdateMerchantDetail,
  needUpdateStoreDetail,
  cancelPendingRequests,
  fetchMerchantBalanceSuccess,
  fetchMerchantBalanceFailed,
}

type ActionCreatorTypes = {
  [K in keyof typeof actionCreator]: ReturnType<typeof actionCreator[K]>
}
export type ActionCreatorTypeList = ActionCreatorTypes[keyof ActionCreatorTypes]

// state
const initialMerchantOutlets = (): MerchantOutlets => ({
  dataReady: false,
  list: [],
  activeTypes: [],
  cities: [],
  modelTypes: [],
  cancelTokenSource: axios.CancelToken.source(),
})

export const initialState = (): MerchantInfo => ({
  store: {
    detail: null,
    error: null,
    fetching: false,
    needToUpdate: false,
  },
  merchant: {
    balance: null,
    detail: null,
    outlets: initialMerchantOutlets(),
    error: null,
    fetching: false,
    needToUpdate: false,
  },
})

// reducer
export const reducer = createReducer<
  MerchantInfo,
  ActionTypes,
  ActionCreatorTypeList
>(initialState(), {
  [RESET_DETAIL]: () => {
    return initialState()
  },
  [FETCH_MERCHANT_DETAIL]: (state: MerchantInfo) => ({
    ...state,
    merchant: {
      ...state.merchant,
      outlets: state.merchant.outlets,
      fetching: true,
      error: null,
      detail: state.merchant.detail,
      needToUpdate: false,
    },
  }),
  [FETCH_MERCHANT_DETAIL_SUCCESS]: (
    state: MerchantInfo,
    action: ActionCreatorTypes["fetchMerchantDetailSuccess"],
  ) => ({
    ...state,
    merchant: {
      ...state.merchant,
      outlets: state.merchant.outlets,
      detail: action.payload.merchantDetail,
      fetching: false,
      error: null,
      needToUpdate: false,
    },
  }),
  [FETCH_MERCHANT_DETAIL_FAILED]: (
    state: MerchantInfo,
    action: ActionCreatorTypes["fetchMerchantDetailFailed"],
  ) => ({
    ...state,
    merchant: {
      ...state.merchant,
      outlets: state.merchant.outlets,
      detail: null,
      todaySummary: null,
      error: action.payload.error,
      fetching: false,
      needToUpdate: false,
    },
  }),
  [NEED_UPDATE_MERCHANT_DETAIL]: (state: MerchantInfo) => {
    if (state.merchant.detail === null) return state
    const newState = { ...state }
    newState.merchant.needToUpdate = true
    return newState
  },
  [NEED_UPDATE_STORE_DETAIL]: (state: MerchantInfo) => {
    if (state.store.detail === null) return state
    const newState = { ...state }
    newState.store.needToUpdate = true
    return newState
  },
  [RESET_MERCHANT_OUTLETS]: (state: MerchantInfo) => {
    return {
      ...state,
      merchant: {
        ...state.merchant,
        detail:
          state.merchant.detail?.cloneWith({
            earnings: 0,
            revenue: 0,
            ordersBreakdown: {
              failed: 0,
              cancelled: 0,
              completed: 0,
            },
          }) ?? null,
        outlets: initialMerchantOutlets(),
      },
    }
  },
  [FETCH_MERCHANT_OUTLETS]: (state: MerchantInfo) => ({
    ...state,
    merchant: {
      ...state.merchant,
      fetching: true,
    },
  }),
  [FETCH_MERCHANT_OUTLETS_SUCCESS]: (
    state: MerchantInfo,
    action: ActionCreatorTypes["fetchMerchantOutletsSuccess"],
  ) => {
    const { outlets } = action.payload
    const { cities, activeTypes, modelTypes } = state.merchant.outlets
    let [revenue, earnings, completedOrders, advertisements] = [0, 0, 0, 0, 0]
    outlets.forEach(outlet => {
      const { city, active, isIntegrated } = outlet
      if (!includes(city, cities)) {
        cities.push(city)
      }
      if (!includes(active, activeTypes)) {
        activeTypes.push(active)
        activeTypes.sort()
      }
      if (!includes(isIntegrated, modelTypes)) {
        modelTypes.push(isIntegrated)
        modelTypes.sort()
      }
      revenue += isIntegrated ? outlet.revenue : 0
      earnings += isIntegrated ? outlet.earnings : 0
      completedOrders += isIntegrated ? outlet.ordersBreakdown.completed : 0
      advertisements += isIntegrated ? outlet.advertisements : 0
    })
    const merchantDetail = state.merchant.detail!.cloneWith({
      revenue,
      earnings,
      advertisements,
      ordersBreakdown: {
        completed: completedOrders,
        failed: 0,
        cancelled: 0,
      },
    })
    cities.sort()
    return {
      ...state,
      merchant: {
        ...state.merchant,
        fetching: false,
        detail: merchantDetail,
        outlets: {
          ...state.merchant.outlets,
          dataReady: true,
          list: outlets,
        },
      },
    }
  },
  [FETCH_MERCHANT_OUTLETS_FAILED]: (
    state: MerchantInfo,
    action: ActionCreatorTypes["fetchMerchantOutletsFailed"],
  ) => ({
    ...state,
    merchant: {
      ...state.merchant,
      error: action.payload.error,
      fetching: false,
      outlets: {
        ...state.merchant.outlets,
        dataReady: false,
      },
    },
  }),
  [FETCH_MERCHANT_BALANCE_SUCCESS]: (
    state,
    action: ActionCreatorTypes["fetchMerchantBalanceSuccess"],
  ) => {
    const balance = action.payload.balance
    return {
      ...state,
      merchant: {
        ...state.merchant,
        balance,
      },
    }
  },
  [FETCH_MERCHANT_BALANCE_FAILED]: state => {
    return {
      ...state,
      merchant: {
        ...state.merchant,
        balance: Number.NaN,
      },
    }
  },
})

// epic
const fetchMerchantDetailEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState,
  EpicDependenciesType
> = (action$, _, { httpRequest }) =>
  action$.pipe(
    ofType(FETCH_MERCHANT_DETAIL),
    switchMap((action: ActionCreatorTypes["fetchMerchantDetail"]) => {
      const { startDate, endDate } = action.payload
      return httpRequest
        .GET<APIGetMerchantDetailV2Resp>({
          endpoint: `${TroyFoodPortalEndpoint.getMerchantGroupsV2}`,
          params: {
            startTime: startDate,
            endTime: endDate,
          },
        })
        .pipe(
          switchMap(res => {
            if (res.data == null || res.data.merchantGroup == null)
              return of(fetchMerchantDetailFailed(CommonAPIError.notFound))
            const merchant = res.data.merchantGroup
            const merchantDetail = merchantDetailV2Convertor(merchant)
            resetDateRangeParams(merchantDetail.timezone)
            return of(fetchMerchantDetailSuccess(merchantDetail))
          }),
        )
    }),
  )

const fetchMerchantOutletsEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  { merchant: MerchantInfo },
  EpicDependenciesType
> = (action$, store$, { httpRequest }) =>
  action$.pipe(
    ofType(FETCH_MERCHANT_OUTLETS),
    switchMap((action: ActionCreatorTypes["fetchMerchantOutlets"]) => {
      const { startDate, endDate } = action.payload
      const cancelTokenSource =
        store$.value.merchant.merchant.outlets.cancelTokenSource
      const fetchMerchantOutletObservable = (count = 0) =>
        httpRequest.GET<APIGetMerchantOutletsResp>({
          endpoint: `${TroyFoodPortalEndpoint.getMerchantGroupsV1}/merchants`,
          params: {
            startTime: startDate,
            endTime: endDate,
            offset: count * API_DEFAULT_LIMIT,
            limit: API_DEFAULT_LIMIT,
          },
          cancelToken: cancelTokenSource.token,
        })
      return fetchMerchantOutletObservable().pipe(
        expand((res, idx) => {
          if (res.data === null) {
            return throwError(new Error())
          }
          const hasMore = res.data?.hasMore ?? false
          if (hasMore) {
            return fetchMerchantOutletObservable(idx + 1)
          }
          return EMPTY
        }),
        reduce((acc, res) => {
          const outlets = (res.data?.merchants || []).map(storeDetailConvertor)
          acc.push(...outlets)
          return acc
        }, [] as Array<StoreDetail>),
        switchMap(outlets => {
          return of(fetchMerchantOutletsSuccess(outlets))
        }),
        catchError(() => of(fetchMerchantOutletsFailed("error"))),
      )
    }),
  )

const cancelPendingRequestsEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  { merchant: MerchantInfo }
> = (action$, store$) =>
  action$.pipe(
    ofType(CANCEL_PENDING_REQUESTS),
    switchMap(() => {
      store$.value.merchant.merchant.outlets.cancelTokenSource.cancel()
      return of(resetMerchantOutlets()).pipe(delay(100))
    }),
  )

const fetchMerchantBalanceV2Epic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState,
  EpicDependenciesType
> = (action$, store$, { httpRequest }) =>
  action$.pipe(
    ofType(FETCH_MERCHANT_DETAIL),
    mergeMap(() => {
      const cancelTokenSource =
        store$.value.merchant.merchant.outlets.cancelTokenSource
      const fetchMerchantBalanceObservable = (count = 0) =>
        httpRequest.GET<APIGetMerchantBalanceV2>({
          endpoint: `${TroyFoodPortalEndpoint.getMerchantGroupsV2}/balance`,
          params: {
            offset: count * API_MERCHANT_BALANCE_LIMIT,
            limit: API_MERCHANT_BALANCE_LIMIT,
          },
          cancelToken: cancelTokenSource.token,
        })
      return fetchMerchantBalanceObservable().pipe(
        expand((res, idx) => {
          if (res.data === null) {
            return throwError(new Error())
          }
          const hasMore = res.data?.hasMore ?? false
          if (hasMore) {
            return fetchMerchantBalanceObservable(idx + 1)
          }
          return EMPTY
        }),
        reduce((totalBalance, res) => {
          const currentBalanceSum = (
            res.data?.merchantBalanceData ?? []
          ).reduce((acc, entity) => acc + (entity.totalBalanceInMin ?? 0), 0)
          return totalBalance + currentBalanceSum
        }, 0),
        switchMap(balance => of(fetchMerchantBalanceSuccess(balance))),
        catchError(() => of(fetchMerchantBalanceFailed())),
      )
    }),
  )

export const epics = {
  fetchMerchantDetailEpic,
  fetchMerchantOutletsEpic,
  fetchMerchantBalanceV2Epic,
  cancelPendingRequestsEpic,
}
